/**
 * PB-FOUNDATION builder tokens — money, token resolution, escape helpers.
 * PB-FND-32: extracted from pb-logic-core.liquid.
 * PB-SBT-1: single selection-lookup seam (flat label -> scoped bundle key).
 */

/**
 * PB-SBT-1 — normalized alias label (mirrors normalizeShopifyBundleFieldLabel).
 * @param {unknown} label
 * @returns {string}
 */
export function normalizeSelectionAliasLabel(label) {
  return String(label == null ? '' : label)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * PB-SBT-1 — resolve a merchant-authored label to the state key that actually holds the answer.
 *
 * Mixed-mode Shopify bundles store answers under `<productId>::<label>` so colliding labels
 * from two components cannot overwrite each other, but merchant copy still writes `{Name}`.
 * The alias index (published on BUILDER_DATA by pb-shopify-bundle-inherit) bridges the two.
 * Same-component match wins; a unique cross-component match is the fallback; ambiguous
 * labels with no scope resolve to nothing rather than showing another product's answer.
 *
 * @param {object|null|undefined} index
 * @param {unknown} label
 * @param {unknown} [scopeProductId]
 * @returns {string}
 */
export function resolveSelectionAliasKey(index, label, scopeProductId) {
  if (!index || typeof index !== 'object') return '';
  const entries = index[normalizeSelectionAliasLabel(label)];
  if (!Array.isArray(entries) || entries.length === 0) return '';
  const scope = scopeProductId == null ? '' : String(scopeProductId);
  if (scope !== '') {
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      if (entry && String(entry.productId == null ? '' : entry.productId) === scope) {
        return String(entry.key || '');
      }
    }
  }
  return entries.length === 1 ? String(entries[0].key || '') : '';
}

function readSelectionAliasIndex(opts) {
  if (opts && opts.aliasIndex) return opts.aliasIndex;
  if (typeof window === 'undefined') return null;
  const data = window.BUILDER_DATA;
  return data && data.shopifyBundleAliasIndex ? data.shopifyBundleAliasIndex : null;
}

function hasSelectionValue(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

/**
 * PB-SBT-1 — the one place selections are read by merchant-authored label.
 * Token substitution, AI Text `depends_on`, and Design Library `depends_on` all route here,
 * so bundle aliasing can never drift between them.
 *
 * @param {object|null|undefined} selections
 * @param {unknown} label
 * @param {{ aliasIndex?: object, scopeProductId?: unknown }} [opts]
 * @returns {*} raw selection value (caller normalizes), or undefined
 */
export function lookupSelectionValue(selections, label, opts) {
  if (!selections || label == null) return undefined;
  const direct = selections[label];
  if (hasSelectionValue(direct)) return direct;
  const key = resolveSelectionAliasKey(readSelectionAliasIndex(opts), label, opts && opts.scopeProductId);
  if (!key || key === label) return direct;
  const aliased = selections[key];
  return hasSelectionValue(aliased) ? aliased : direct;
}

/**
 * @param {object} deps
 * @param {object} deps.pb — productBuilder instance
 */
export function createBuilderTokens(deps) {
  const pb = deps.pb || deps;

  function escapeAttr(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function cleanMoney(str) {
    if (!str) return '';
    let s = String(str);
    if (/<[^>]+>/.test(s)) s = s.replace(/<[^>]*>/g, '');
    return s.replace(/\.00(?=\D|$)/, '');
  }

  function parsePrice(p) {
    if (typeof p === 'number') return p;
    if (!p) return 0;
    const clean = String(p).replace(/[^0-9.]/g, '');
    const val = parseFloat(clean);
    if (isNaN(val)) return 0;
    return Math.round(val * 100);
  }

  function formatMoney(cents) {
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.formatMoney) {
      return cleanMoney(window.Shopify.formatMoney(cents));
    }
    let val = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    val = val.replace(/\.00$/, '');
    if (typeof window !== 'undefined' && window.BUILDER_DATA?.mainProduct?.price_formatted) {
      const plain = cleanMoney(window.BUILDER_DATA.mainProduct.price_formatted);
      const symbolMatch = plain.match(/^[^0-9]*/);
      return (symbolMatch ? symbolMatch[0] : '') + val;
    }
    return val;
  }

  function formatMoneyWithCurrency(cents) {
    const fmt = typeof window !== 'undefined' && window.BUILDER_DATA?.shopMoneyWithCurrencyFormat;
    if (typeof window !== 'undefined' && window.Shopify?.formatMoney && fmt) {
      return cleanMoney(window.Shopify.formatMoney(cents, fmt));
    }
    return formatMoney(cents);
  }

  function isBundlePercentageDiscount(discountType) {
    const t = String(discountType == null ? '' : discountType).trim().toLowerCase();
    return t === '%' || t === 'percent' || t === 'percentage' || t === 'pct';
  }

  function calcBundleDiscountCents(totalSellingCents, discountVal, discountType) {
    const total = Number(totalSellingCents) || 0;
    const val = Number(discountVal);
    if (!Number.isFinite(val) || val < 0) return NaN;
    if (isBundlePercentageDiscount(discountType)) {
      return Math.round(total * (val / 100));
    }
    // Metaobject fixed discounts are authored in shop currency; variants are already in presentment currency.
    const currency = typeof window !== 'undefined' ? window.Shopify?.currency : null;
    const rate = currency?.active ? Number(currency.rate) : 1;
    if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(val) || val < 0) return NaN;
    return Math.round(val * rate * 100);
  }

  function resolveCtaButtonPriceTokens(template, variantCents) {
    let str = String(template ?? '');
    if (!str.includes('{')) return str;
    let out = str;
    if (out.includes('{price_with_currency}')) {
      out = out.split('{price_with_currency}').join(formatMoneyWithCurrency(variantCents));
    }
    if (out.includes('{price}')) {
      out = out.split('{price}').join(formatMoney(variantCents));
    }
    return out;
  }

  function escapeSelector(value) {
    const str = String(value ?? '');
    if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(str);
    }
    return str.replace(/["\\]/g, '\\$&');
  }

  function resolvePersonalizationTokens(text, ctx, opts) {
    const str = String(text ?? '');
    if (!str.includes('{')) return str;
    const selections = ctx?.selections || {};
    return str.replace(/\{([^}]+)\}/g, (match, label) => {
      const key = String(label || '').trim();
      if (!key) return '';
      const val = lookupSelectionValue(selections, key, opts);
      if (val == null) return '';
      return String(val);
    });
  }

  /** PB-SBT-1 — component a template belongs to, so `{Name}` resolves to that component's answer. */
  function resolveTemplateScopeProductId(el) {
    if (!el || typeof el.closest !== 'function') return '';
    const host = el.closest('[data-pb-scope-product-id]');
    return host ? host.getAttribute('data-pb-scope-product-id') || '' : '';
  }

  /**
   * The centered question — shown when a field has no visual — fades in word by
   * word, like the AI Text preview. Its step is display:none until active, so
   * the CSS animation starts on its own when the step is shown. Everything else
   * keeps plain textContent.
   */
  function setTemplateText(el, text) {
    const reveal = typeof globalThis !== 'undefined' && globalThis.PBTextReveal;
    if (reveal && el.classList && el.classList.contains('pb-question-highlight-text')) {
      reveal.revealWords(el, text, {
        // First view only — stepping back to a question already read should not
        // make the customer wait through it again.
        once: true,
        // No cap: every word keeps the same gap, so a short question is quick
        // and a long one simply takes longer.
        capWindowMs: 0,
        startDelayMs: reveal.REVEAL_START_OFFSET_MS,
      });
      return;
    }
    el.textContent = text;
  }

  function syncPersonalizationTemplates(container) {
    const ctx = typeof pb.getActiveContext === 'function' ? pb.getActiveContext() : null;
    const root = typeof container === 'string' ? document.querySelector(container) : container;
    if (!root) return;
    root.querySelectorAll('[data-pb-template]').forEach((el) => {
      const opts = { scopeProductId: resolveTemplateScopeProductId(el) };
      if (el.dataset.pbValueTarget === 'topic-prompt') {
        const template = el.dataset.pbTemplate || '';
        const resolved = resolvePersonalizationTokens(template, ctx, opts);
        setTemplateText(
          el,
          typeof pb.getTopicPrompt === 'function' ? pb.getTopicPrompt(resolved) : resolved,
        );
        return;
      }
      const template = el.dataset.pbTemplate || '';
      const resolved = resolvePersonalizationTokens(template, ctx, opts);
      const transform = el.dataset.pbTransform || '';
      const finalText = transform === 'upper' ? resolved.toUpperCase() : resolved;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        el.placeholder = finalText;
      } else {
        setTemplateText(el, finalText);
      }
      if (el.dataset.pbValueTarget === 'self') {
        el.setAttribute('data-value', finalText);
      }
    });
    root.querySelectorAll('.pb-question-highlight-desc[data-pb-template]').forEach((el) => {
      const template = el.dataset.pbTemplate || '';
      el.textContent = resolvePersonalizationTokens(template, ctx, {
        scopeProductId: resolveTemplateScopeProductId(el),
      });
    });
  }

  return {
    escapeAttr,
    cleanMoney,
    parsePrice,
    formatMoney,
    formatMoneyWithCurrency,
    isBundlePercentageDiscount,
    calcBundleDiscountCents,
    resolveCtaButtonPriceTokens,
    escapeSelector,
    resolvePersonalizationTokens,
    syncPersonalizationTemplates,
  };
}

const TOKEN_METHODS = [
  'escapeAttr',
  'cleanMoney',
  'parsePrice',
  'formatMoney',
  'formatMoneyWithCurrency',
  'isBundlePercentageDiscount',
  'calcBundleDiscountCents',
  'resolveCtaButtonPriceTokens',
  'escapeSelector',
  'resolvePersonalizationTokens',
  'syncPersonalizationTemplates',
];

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBBuilderTokens = {
    createBuilderTokens,
    lookupSelectionValue,
    resolveSelectionAliasKey,
    normalizeSelectionAliasLabel,
  };
}

function install(pb) {
  if (!pb || !window.PBBuilderTokens) return false;
  if (pb._pbTokensInstalled) return true;
  const tokens = createBuilderTokens({ pb });
  pb._pbTokensInstalled = true;
  pb._pbTokens = tokens;
  TOKEN_METHODS.forEach((name) => {
    pb[name] = function (...args) {
      return tokens[name](...args);
    };
  });
  return true;
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  return install(window.productBuilder);
}

attachToWindow();

if (typeof window !== 'undefined') {
  window.__pbEnsureBuilderTokens = install;
  if (!tryInstall()) {
    document.addEventListener('DOMContentLoaded', tryInstall);
    document.addEventListener('builder:ready', tryInstall);
  }
}
